import React, { useEffect, useMemo, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Library, Plus, Check, Search, X } from "lucide-react";
import Ref from "@/components/Ref";

/**
 * C3a · item 10 — "Add head from library".
 *
 * Lists every FS head from the 80-head taxonomy grouped by section
 * (SFP non-current assets / SFP current assets / SFP equity / ...).
 * Heads that are already present on the engagement (either derived
 * from `coa_mappings` or previously added) are shown disabled.
 *
 * Adding a head calls `POST /head-folders/add-head`, which logs the
 * action in W13 and returns immediately. The parent refreshes the
 * head-folder tree via `onAdded`.
 */
export default function AddHeadFromLibraryDialog({ open, onOpenChange, onAdded }) {
  const { t, lang } = useI18n();
  const { currentEngId } = useEngagement();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [pendingCode, setPendingCode] = useState(null);

  useEffect(() => {
    if (!open || !currentEngId) return;
    let alive = true;
    api.get(`/engagements/${currentEngId}/head-folders/library`, { _silent: true })
      .then((r) => { if (alive) setRows(r.data?.heads || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [open, currentEngId]);

  const filtered = useMemo(() => {
    const list = rows || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((h) =>
      h.fs_code.toLowerCase().includes(needle)
      || (h.label || "").toLowerCase().includes(needle)
      || (h.label_ar || "").toLowerCase().includes(needle));
  }, [rows, q]);

  const groups = useMemo(() => {
    const g = new Map();
    for (const h of filtered) {
      const key = lang === "ar" ? (h.section_ar || h.section || "") : (h.section || "");
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(h);
    }
    return Array.from(g.entries());
  }, [filtered, lang]);

  const add = async (h) => {
    setPendingCode(h.fs_code);
    try {
      await api.post(`/engagements/${currentEngId}/head-folders/add-head`,
                     { fs_code: h.fs_code }, { _silent: true });
      toast.success(t("headFolders.addedOk").replace("{code}", h.fs_code));
      // Refresh local list so the row now shows already_present.
      const r = await api.get(`/engagements/${currentEngId}/head-folders/library`, { _silent: true });
      setRows(r.data?.heads || []);
      onAdded?.(h);
    } catch (e) {
      const code = e?.response?.data?.detail?.code || e?.response?.data?.code;
      if (code === "HEAD_ALREADY_PRESENT") {
        toast.error(t("headFolders.alreadyPresent"));
      } else {
        toast.error(toErrMsg(e, t("headFolders.addFailed")));
      }
    } finally { setPendingCode(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-head-from-library-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Library className="h-5 w-5" />
            {t("headFolders.addHeadFromLibraryTitle")}
          </DialogTitle>
          <DialogDescription>{t("headFolders.addHeadFromLibraryDesc")}</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute start-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder={t("headFolders.searchPlaceholder")}
                 className="h-9 w-full ps-7 pe-6 text-sm"
                 data-testid="add-head-search"
                 autoFocus />
          {q && (
            <button type="button"
                    onClick={() => setQ("")}
                    className="absolute end-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    data-testid="add-head-search-clear">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {rows === null && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            {t("docLib.loading")}
          </div>
        )}
        {rows !== null && filtered.length === 0 && (
          <div className="py-6 text-center text-sm text-muted-foreground"
               data-testid="add-head-empty">
            {t("headFolders.libraryEmpty")}
          </div>
        )}
        {rows !== null && filtered.length > 0 && (
          <div className="space-y-4" data-testid="add-head-groups">
            {groups.map(([groupName, hs]) => (
              <div key={groupName}>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                     data-testid={`add-head-group-${groupName || "misc"}`}>
                  {groupName}
                </div>
                <ul className="divide-y rounded-md border">
                  {hs.map((h) => (
                    <li key={h.fs_code}
                        className="flex items-center gap-3 px-3 py-2 text-sm"
                        data-testid={`add-head-row-${h.fs_code}`}>
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                        <Ref>{h.fs_code}</Ref>
                      </span>
                      <span className="min-w-0 flex-1 break-words">
                        {(lang === "ar" ? h.label_ar : h.label) || h.label}
                      </span>
                      {h.already_present ? (
                        <Badge variant="outline" className="gap-1 text-[10px]"
                               data-testid={`add-head-present-${h.fs_code}`}>
                          <Check className="h-3 w-3" /> {t("headFolders.alreadyPresent")}
                        </Badge>
                      ) : (
                        <Button size="sm" variant="outline"
                                className="h-7 gap-1"
                                disabled={pendingCode === h.fs_code}
                                onClick={() => add(h)}
                                data-testid={`add-head-add-${h.fs_code}`}>
                          <Plus className="h-3 w-3" />
                          {pendingCode === h.fs_code ? t("docLib.adding") : t("headFolders.addBtn")}
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
